import { FC, useEffect, useState } from 'react';

import { Box } from '@bonusly/design-system';

import { useLocalStorage } from '../../../lib/storage/hooks/use-local-storage';
import { useIsAdminRoute } from '../use-is-admin-route';

interface Particle {
	id: number;
	left: number;
	animationDuration: number;
	animationDelay: number;
	size: number;
	emoji: string;
	rotation: number;
	swayAmount: number;
}

export const FallingParticles: FC = () => {
	const isAdminRoute = useIsAdminRoute();
	const [showFallingEmojis] = useLocalStorage('bonusly:seasonal-theme:summer:show-falling-emojis', true);
	const [particles, setParticles] = useState<Particle[]>([]);

	useEffect(() => {
		if (!showFallingEmojis) return;
		const generated: Particle[] = [];

		for (let i = 0; i < 8; i++) {
			const rand = Math.random();
			let emoji: string;
			if (rand < 0.35) {
				emoji = '🍉';
			} else if (rand < 0.55) {
				emoji = '🍦';
			} else if (rand < 0.75) {
				emoji = '🍋';
			} else if (rand < 0.9) {
				emoji = '🍧';
			} else {
				emoji = '🍍';
			}

			generated.push({
				id: i,
				left: Math.random() * 100,
				animationDuration: 12 + Math.random() * 14,
				animationDelay: Math.random() * 18,
				size: 16 + Math.random() * 14,
				emoji,
				rotation: Math.random() * 360,
				swayAmount: 30 + Math.random() * 70,
			});
		}

		setParticles(generated);
	}, []);

	if (isAdminRoute || !showFallingEmojis) return null;

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			width="100vw"
			height="100vh"
			pointerEvents="none"
			zIndex={9999}
			overflow="hidden"
			opacity={0}
			animation="summerFadeIn 1.5s ease-in forwards"
			sx={{
				'@keyframes summerFadeIn': {
					from: { opacity: 0 },
					to: { opacity: 1 },
				},
			}}
		>
			{particles.map((particle) => (
				<Box
					key={particle.id}
					position="absolute"
					top={-50}
					left={`${particle.left}%`}
					fontSize={`${particle.size}px`}
					opacity={0.65}
					style={{
						animation: `summer-fall-${particle.id} ${particle.animationDuration}s linear ${particle.animationDelay}s infinite`,
						transform: `rotate(${particle.rotation}deg)`,
					}}
				>
					<style>
						{`
							@keyframes summer-fall-${particle.id} {
								0% {
									transform: translateY(-50px) translateX(0px) rotate(${particle.rotation}deg);
									opacity: 0;
								}
								5% {
									opacity: 0.65;
								}
								25% {
									transform: translateY(25vh) translateX(${particle.swayAmount}px) rotate(${particle.rotation + 90}deg);
								}
								50% {
									transform: translateY(50vh) translateX(-${particle.swayAmount * 0.3}px) rotate(${particle.rotation + 180}deg);
								}
								75% {
									transform: translateY(75vh) translateX(${particle.swayAmount * 0.6}px) rotate(${particle.rotation + 270}deg);
								}
								95% {
									opacity: 0.65;
								}
								100% {
									transform: translateY(100vh) translateX(0px) rotate(${particle.rotation + 360}deg);
									opacity: 0;
								}
							}
						`}
					</style>
					{particle.emoji}
				</Box>
			))}
		</Box>
	);
};
